import * as stylex from '@stylexjs/stylex';
import { useSuspenseQuery, useMutation } from '@tanstack/react-query';
import { CatchBoundary, createFileRoute, redirect } from '@tanstack/react-router';
import { type ReactNode } from 'react';
import { MenuTrigger, Pressable } from 'react-aria-components';
import { pick as rPick } from 'remeda';
import {
  number as vNumber,
  parse as vParse,
  pipe as vPipe,
  string as vString,
  transform as vTransform,
} from 'valibot';

import { SecondaryActionsIconButton } from '@/components/secondary-actions-icon-button.component';
import { theme } from '@/constants/theme';
import { CampaignRequestProgramStageEnum } from '@/libs/openapi';
import {
  Badge,
  ChevronDownSmallStrokeIcon,
  Menu,
  MenuItem,
  Popover,
  Text,
  Breadcrumb,
  Breadcrumbs,
  EditStrokeIcon,
  View,
} from '@/libs/serenity-ui';
import { RouterLink } from '@/libs/tanstack-router';
import { auditLogQueries } from '@/resources/audit-log/audit-log.api';
import { auditLogPolicies } from '@/resources/audit-log/audit-log.policies';
import { campaignRequestQueries } from '@/resources/campaign-request/campaign-request.api';
import { campaignRequestProgramMutations, campaignRequestProgramQueries } from '@/resources/campaign-request-program/campaign-request-program.api';
import { campaignRequestProgramPolicies } from '@/resources/campaign-request-program/campaign-request-program.policies';
import { CampaignRequestsWidget } from '@/routes/_main/_details/programs/-components/campaign-requests-widget.component';
import { CustomerAuditLogWidget } from '@/routes/_main/_details/programs/-components/customer-audit-log-widget.component';
import { DetailsWidget } from '@/routes/_main/_details/programs/-components/details-widget.component';
import { SettingsWidget } from '@/routes/_main/_details/programs/-components/settings-widget.component';
import { CopyIdToolbarTextButton } from '@/routes/_main/_form/-components/copy-id-toolbar-text-button.component';
import { Resource } from '@/types/resource.type';
import { formatEnumValue } from '@/utils/format.util';

export const Route = createFileRoute('/_main/_details/programs/$programId')({
  component: RouteComponent,
  params: {
    parse: ({ programId }) => ({ programId: vParse(vPipe(vString(), vTransform(Number), vNumber()), programId) }),
    stringify: ({ programId }) => ({ programId: String(programId) }),
  },
  staticData: {
    details: {
      heading: HeadingComponent,
      pageType: 'Program',
      slots: {
        topBarComponent: TopBarComponent,
        idComponent: IdComponent,
        statusComponent: StatusComponent,
        actionsComponent: ActionsComponent,
      },
    },
  },
  beforeLoad: async ({ context, params }) => {
    if (context.user && !context.programsFeatureEnabled) {
      throw redirect({ to: '/campaigns' });
    }

    if (context.user && !context.can.campaignRequestPrograms.view()) {
      throw redirect({ to: '/' });
    }

    return {
      resource: Resource.campaignRequestProgram as const,
      queryOptions: {
        detail: campaignRequestProgramQueries.detail(params.programId),
        auditLogList: auditLogQueries.list({ id: params.programId, resource: Resource.campaignRequestProgram, limit: 10, page: 1 }),
        campaignRequestList: campaignRequestQueries.list({
          filter: { programId: params.programId },
          paging: { pageIndex: 0, pageSize: 50 },
        }),
      },
      can: {
        [Resource.campaignRequestProgram as const]: rPick(
          campaignRequestProgramPolicies.bindContext({ user: context.user.original, organization: context.organization?.original }),
          [ 'create', 'update', 'updateOwner' ],
        ),
        [Resource.activityLog as const]: rPick(
          auditLogPolicies.bindContext({ user: context.user.original }),
          [ 'view' ],
        ),
      },
    };
  },
  loader: async ({ context }) => {
    if (!context.user) {
      return undefined;
    }

    const detail = await context.queryClient.ensureQueryData(context.queryOptions.detail);

    context.queryClient.prefetchQuery(context.queryOptions.campaignRequestList);

    if (context.can.activityLog.view()) {
      context.queryClient.prefetchQuery(context.queryOptions.auditLogList);
    }

    return { detail };
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: `TrendMD | ${loaderData?.detail.name ?? 'Program details'}`,
      },
    ],
  }),
});

function RouteComponent(): ReactNode {
  const routeContext = Route.useRouteContext();

  return (
    <>
      <View>
        <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error) => console.error(error)}>
          <DetailsWidget />
        </CatchBoundary>
        <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error) => console.error(error)}>
          <SettingsWidget />
        </CatchBoundary>
      </View>

      <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error) => console.error(error)}>
        <CampaignRequestsWidget />
      </CatchBoundary>

      {routeContext.can.activityLog.view() && (
        <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error) => console.error(error)}>
          <CustomerAuditLogWidget />
        </CatchBoundary>
      )}
    </>
  );
}

function TopBarComponent(): ReactNode {
  return (
    <Breadcrumbs>
      <Breadcrumb>
        <RouterLink to="/programs">Programs</RouterLink>
      </Breadcrumb>
      <Breadcrumb hidden>
        <RouterLink to=".">Program</RouterLink>
      </Breadcrumb>
    </Breadcrumbs>
  );
}

function HeadingComponent(): ReactNode {
  const routeContext = Route.useRouteContext();
  const detailQuery = useSuspenseQuery(routeContext.queryOptions.detail);
  return detailQuery.data.original.name;
}

function IdComponent(): ReactNode {
  const routeParams = Route.useParams();
  return <CopyIdToolbarTextButton id={routeParams.programId} />;
}

function StatusComponent(): ReactNode {
  const routeContext = Route.useRouteContext();
  const routeParams = Route.useParams();
  const detailQuery = useSuspenseQuery(routeContext.queryOptions.detail);

  const canUpdate = routeContext.can.campaignRequestProgram.update(detailQuery.data.original);

  const stageMutation = useMutation(campaignRequestProgramMutations.changeStage);

  if (!(canUpdate && detailQuery.data.original.stageTransitions?.length)) {
    return (
      <Badge {...theme.badge[detailQuery.data.transformed.forLabels.stage]} size={32}>
        <Text>{detailQuery.data.transformed.forLabels.stage}</Text>
      </Badge>
    );
  }

  return (
    <MenuTrigger>
      <Pressable>
        <Badge role="button" style={badgeStyles.interactive} {...theme.badge[detailQuery.data.transformed.forLabels.stage]} isRounded size={32}>
          <Text>{detailQuery.data.transformed.forLabels.stage}</Text>
          <ChevronDownSmallStrokeIcon color={theme.badge[detailQuery.data.transformed.forLabels.stage].color} size={20} slot={null!} />
        </Badge>
      </Pressable>
      <Popover placement="bottom start">
        <Menu
          disabledKeys={stageMutation.isPending ? detailQuery.data.original.stageTransitions.map(String) : []}
          onAction={(key) => stageMutation.mutate( { id: routeParams.programId, body: { stage: Number(key) }})}>
          {detailQuery.data.original.stageTransitions.map((targetStage) => (
            <MenuItem id={String(targetStage)} key={targetStage}>
              <Text>{formatEnumValue(CampaignRequestProgramStageEnum)(targetStage)}</Text>
            </MenuItem>
          ))}
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}

const badgeStyles = stylex.create({
  interactive: {
    cursor: 'pointer',
  },
});

function ActionsComponent(): ReactNode {
  const routeParams = Route.useParams();
  const routeContext = Route.useRouteContext();
  const navigate = Route.useNavigate();
  const detailQuery = useSuspenseQuery(routeContext.queryOptions.detail);

  const canEdit = routeContext.can.campaignRequestProgram.update(detailQuery.data.original);

  return (
    <SecondaryActionsIconButton id={routeParams.programId}>
      {canEdit && (
        <MenuItem onAction={() => navigate({ to: '/programs/$programId/edit', params: { programId: routeParams.programId }})}>
          <EditStrokeIcon />
          <Text>Edit</Text>
        </MenuItem>
      )}
    </SecondaryActionsIconButton>
  );
}
